if (!customElements.get('cart-drawer-upsell')) {
  customElements.define(
    'cart-drawer-upsell',
    class CartDrawerUpsell extends HTMLElement {
      static cache = new Map();

      /**
       * Identifies the current cart by the products in it, read from the rendered
       * lines so this costs no request. Quantities are deliberately excluded — they
       * do not change which products the section filters out.
       * @returns {string}
       */
      static cartKey() {
        const links = document.querySelectorAll('#CartDrawer .cart-item__name');
        return [...links]
          .map((link) => link.getAttribute('href')?.split('?')[0] ?? '')
          .sort()
          .join(',');
      }

      connectedCallback() {
        this.addEventListener('click', this.onQuickBuy);
        this.drawer = this.closest('cart-drawer');
        if (!this.drawer || !(this.dataset.url || this.dataset.wishlistSection)) return;

        if (this.drawer.classList.contains('active')) {
          this.load();
        } else {
          // Defer the recommendations request until the drawer first opens.
          this.observer = new MutationObserver(() => {
            if (!this.drawer.classList.contains('active')) return;
            this.observer.disconnect();
            this.observer = null;
            this.load();
          });
          this.observer.observe(this.drawer, { attributes: true, attributeFilter: ['class'] });
        }
      }

      /**
       * Saved products without a variant open Swish's quick buy dialog to pick one;
       * the add then refreshes this drawer through intents.createCartLine.afterComplete
       * (snippets/swish-options.liquid). Without Swish the link opens the product page.
       * @param {MouseEvent} event
       */
      onQuickBuy = (event) => {
        const trigger = event.target.closest('[data-quick-buy]');
        if (!trigger || !window.swish?.intents) return;
        event.preventDefault();
        // The first available variant is pre-selected (intents.initiateQuickBuy.
        // promptVariantIfPresent keeps the dialog open) so a sold-out first
        // option doesn't greet the shopper with a disabled button.
        window.swish.intents.invoke('initiate:quick-buy', {
          productId: trigger.dataset.quickBuy,
          variantId: trigger.dataset.quickBuyVariant || undefined,
        });
      };

      disconnectedCallback() {
        this.observer?.disconnect();
        this.observer = null;
        this.controller?.abort();
        this.cleanup?.();
        if (this.frame) cancelAnimationFrame(this.frame);
      }

      /**
       * The shopper's own products come first: their wishlist, then (in the empty cart,
       * which sets data-recent-heading) what they last looked at, and only then the
       * product recommendations for the newest cart line.
       */
      async load() {
        const sources = ['saved'];
        if (this.dataset.recentHeading) sources.push('recent');
        for (const source of sources) {
          const markup = await this.loadSwishProducts(source);
          if (!this.isConnected) return;
          if (markup) {
            this.render(markup, source === 'saved' ? 'wishlist' : 'recent');
            return;
          }
        }
        if (this.dataset.url) await this.loadRecommendations();
      }

      /**
       * Renders the rail from the shopper's saved or recently viewed products that are
       * not already in the cart (assets/swish-wishlist.js). Each card is the product's
       * own Section Rendering response, so it is the same Liquid card as the
       * recommendations.
       * @param {'saved' | 'recent'} source
       * @returns {Promise<string>} Rail markup, or '' when there is nothing to show.
       */
      async loadSwishProducts(source) {
        const section = this.dataset.wishlistSection;
        const template = document.getElementById('CartDrawerWishlistRail');
        if (!section || !template || !window.addoWishlist) return '';

        const inCart = new Set(CartDrawerUpsell.cartKey().split(',').map((path) => path.split('/').pop()));
        const products = (await window.addoWishlist.savedProducts(12, source))
          .filter(({ handle }) => !inCart.has(handle))
          .slice(0, 6);
        if (!products.length) return '';

        const key = `${source}|${products.map(({ handle, variantId }) => `${handle}:${variantId ?? ''}`).join(',')}`;
        const cached = CartDrawerUpsell.cache.get(key);
        if (cached !== undefined) return cached;

        const root = window.Shopify?.routes?.root ?? '/';
        const cards = await Promise.all(
          products.map(async ({ handle, variantId }) => {
            const params = new URLSearchParams({ section_id: section });
            if (variantId) params.set('variant', variantId);
            try {
              const response = await fetch(`${root}products/${encodeURIComponent(handle)}?${params}`);
              if (!response.ok) return '';
              const html = new DOMParser().parseFromString(await response.text(), 'text/html');
              return html.querySelector('.cart-upsell__card')?.outerHTML ?? '';
            } catch {
              return '';
            }
          })
        );
        if (!cards.some(Boolean)) return '';

        const rail = template.content.cloneNode(true);
        rail.querySelector('.cart-upsell__track').innerHTML = cards.join('');
        if (source === 'recent') rail.querySelector('.cart-upsell__heading').textContent = this.dataset.recentHeading;
        const wrapper = document.createElement('div');
        wrapper.append(rail);
        CartDrawerUpsell.cache.set(key, wrapper.innerHTML);
        return wrapper.innerHTML;
      }

      async loadRecommendations() {
        // cart-items.js replaces the whole <cart-drawer-items> subtree on every quantity
        // change, so this element is rebuilt — and would re-request — on each press of
        // a stepper. Serve repeats from a cache keyed on the URL plus the set of
        // products in the cart: the section filters out anything already in the cart,
        // so quantity edits can reuse a response but adding or removing a line cannot.
        const url = this.dataset.url;
        const key = `${url}|${CartDrawerUpsell.cartKey()}`;
        const cached = CartDrawerUpsell.cache.get(key);
        if (cached !== undefined) {
          this.render(cached);
          return;
        }

        this.controller?.abort();
        this.controller = new AbortController();

        try {
          const response = await fetch(url, { signal: this.controller.signal });
          if (!response.ok) throw new Error(`Recommendations request failed: ${response.status}`);
          const text = await response.text();
          const section = new DOMParser().parseFromString(text, 'text/html').querySelector('.shopify-section');
          const markup = section?.querySelector('.cart-upsell__card') ? section.innerHTML : '';
          CartDrawerUpsell.cache.set(key, markup);
          this.render(markup);
        } catch (error) {
          if (error.name !== 'AbortError') console.error(error);
        }
      }

      /**
       * @param {string} markup
       * @param {'wishlist' | 'recent' | 'recommendations'} [source] - Exposed as
       *   data-source; phones show only the shopper's own products.
       */
      render(markup, source = 'recommendations') {
        if (!markup) return;
        this.dataset.source = source;
        this.innerHTML = markup;
        this.removeAttribute('hidden');
        this.initNav();
      }

      initNav() {
        const track = this.querySelector('.cart-upsell__track');
        const nav = this.querySelector('.cart-upsell__nav');
        const prev = this.querySelector('[data-upsell-prev]');
        const next = this.querySelector('[data-upsell-next]');
        if (!track || !nav) return;

        // A whole page of cards, snapped down to a card boundary. Stepping by one card
        // left a fractional remainder at the end of the track, so the final click only
        // moved a sliver and the shopper had to press twice to reach the last card.
        /** @returns {{distance: number, cardWidth: number}} */
        const metrics = () => {
          const card = track.querySelector('.cart-upsell__card');
          if (!card) return { distance: track.clientWidth, cardWidth: track.clientWidth };
          const styles = getComputedStyle(track);
          const gap = parseFloat(styles.columnGap) || 0;
          const cardWidth = card.getBoundingClientRect().width;
          const slot = cardWidth + gap;
          const visible =
            track.clientWidth - parseFloat(styles.paddingLeft) - parseFloat(styles.paddingRight);
          return { distance: Math.max(1, Math.floor((visible + gap) / slot)) * slot, cardWidth };
        };
        const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

        // Absorb a short remainder into the current click rather than leaving it for
        // the next one. Checking the distance still left *after* the step is what
        // matters: stopping a sliver short of either end looks identical to being
        // there, so the extra click that closes the gap reads as a dead press.
        const scrollByStep = (direction) => {
          const { distance, cardWidth } = metrics();
          const maxScroll = track.scrollWidth - track.clientWidth;
          const target = track.scrollLeft + direction * distance;
          // What a further click would still have to cover. Under one card it is not
          // worth a press of its own, so this click swallows it.
          const leftover = direction > 0 ? maxScroll - target : target;
          const left =
            leftover < cardWidth
              ? direction > 0
                ? maxScroll
                : 0
              : Math.min(Math.max(target, 0), maxScroll);
          track.scrollTo({ left, behavior });
        };

        prev.addEventListener('click', () => scrollByStep(-1));
        next.addEventListener('click', () => scrollByStep(1));

        const update = () => {
          this.frame = null;
          const maxScroll = track.scrollWidth - track.clientWidth;
          nav.hidden = maxScroll <= 1;
          prev.disabled = track.scrollLeft <= 1;
          next.disabled = track.scrollLeft >= maxScroll - 1;
        };

        // A single smooth scroll fires ~30 events, each one reading scrollWidth and
        // clientWidth. Coalescing to one read per frame keeps that off the scroll path.
        const scheduleUpdate = () => {
          if (this.frame) return;
          this.frame = requestAnimationFrame(update);
        };

        this.cleanup?.();
        track.addEventListener('scroll', scheduleUpdate, { passive: true });
        window.addEventListener('resize', scheduleUpdate, { passive: true });
        this.cleanup = () => {
          track.removeEventListener('scroll', scheduleUpdate);
          window.removeEventListener('resize', scheduleUpdate);
        };
        update();
      }
    }
  );
}
